/**
 * Traducción de tokens a propiedades CSS personalizadas (doc 0390, FT-4.2).
 *
 * POR QUÉ VARIABLES CSS Y NO SOLO EL OBJETO DE TEMA
 *
 * Cambiar una variable en `:root` repinta la aplicación sin volver a renderizar
 * el árbol de React. Con 55 páginas y componentes de chat que mantienen estado
 * (scroll, borradores, reproductores), un re-render global al cambiar de tema o
 * de modo sería visible y molesto.
 *
 * Además son el puente para retirar los 3.797 colores escritos a mano: un
 * `#4b5563` puede pasar a `var(--wtx-text-secondary)` archivo por archivo, sin
 * big-bang y sin tocar el resto.
 */

/** Prefijo de producto. Evita colisiones con variables de terceros. */
export const VAR_PREFIX = "--wtx-";

export const cssVarName = tokenKey => `${VAR_PREFIX}${tokenKey}`;

/**
 * Construye el mapa de variables para un modo concreto.
 *
 * Los tokens con `mode: "all"` valen para claro y oscuro (tipografía, medidas,
 * radios…). Los de color llegan por duplicado —`light` y `dark`— y aquí se
 * escoge la mitad que toca: al cambiar de modo se reescribe el mismo nombre de
 * variable con el otro valor, y no hace falta que ningún componente sepa en qué
 * modo está.
 *
 * @param {Array<{tokenKey:string, mode:string, tokenValue:string}>} tokens
 * @param {"light"|"dark"} mode
 * @returns {Object<string,string>}
 */
export function buildCssVars(tokens, mode) {
  const efectivo = mode === "dark" ? "dark" : "light";
  const vars = {};

  (tokens || []).forEach(token => {
    if (!token || !token.tokenKey) return;
    if (token.mode !== "all" && token.mode !== efectivo) return;
    vars[cssVarName(token.tokenKey)] = token.tokenValue;
  });

  return vars;
}

/**
 * Escribe las variables en el elemento raíz.
 *
 * Se retiran las que ya no estén en el mapa nuevo: sin esa limpieza, cambiar a un
 * tema que define menos tokens dejaría vivas las del anterior, y el resultado
 * sería una mezcla de dos temas imposible de diagnosticar.
 *
 * @returns {number} cuántas variables quedaron aplicadas
 */
export function applyCssVars(vars, target) {
  const raiz =
    target || (typeof document !== "undefined" ? document.documentElement : null);

  if (!raiz || !raiz.style) return 0;

  const previas = [];
  for (let i = 0; i < raiz.style.length; i += 1) {
    const nombre = raiz.style[i];
    if (nombre.startsWith(VAR_PREFIX)) previas.push(nombre);
  }

  previas.forEach(nombre => {
    if (!(nombre in vars)) raiz.style.removeProperty(nombre);
  });

  Object.keys(vars).forEach(nombre => {
    raiz.style.setProperty(nombre, vars[nombre]);
  });

  return Object.keys(vars).length;
}

/**
 * Atajo: construir y aplicar de una vez.
 */
export function applyTokens(tokens, mode, target) {
  return applyCssVars(buildCssVars(tokens, mode), target);
}

export default applyTokens;
